import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Diamond, ArrowRight, Sparkles, Heart, Gift, Anchor, Briefcase, Plus } from "lucide-react";
import Logo from "@/components/Logo";
import YachtsFooter from "@/components/YachtsFooter";
import { getLangFromWindow, setLangInWindowUrl } from "@/utils/seoLang";
import axios from "axios";
import WeddingMobileMenu from "@/components/weddings/WeddingMobileMenu";
import HubLeadForm from "@/components/HubLeadForm";

const WA_PHONE = "905316224378";
const WA_LINK = `https://wa.me/${WA_PHONE}`;

const IMG = {
  hero: "/yachts/hero.webp",
  bodrum: "https://static.prod-images.emergentagent.com/jobs/1bb108bd-28ee-461f-be8a-269d89666c48/images/81a8b14e5e48c735101a0a8b1a9b888976cb2400cc7eae8ce21af14e318fe60c.png",
  mykonos: "https://static.prod-images.emergentagent.com/jobs/1bb108bd-28ee-461f-be8a-269d89666c48/images/177ad39fbcb9809c155bacc4dd75926b60a3e5aa529c95305dff04deecd5e961.png",
  monaco: "https://images.unsplash.com/photo-1575540121783-60f174302cc2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "saint-tropez": "https://images.unsplash.com/photo-1560612137-e423867d48d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
};

const YACHT_CITIES = [
  { slug: "bodrum", name: "Бодрум", nameEn: "Bodrum", country: { ru: "Турция", en: "Turkey" }, image: IMG.bodrum, sym: "$", loc: { ru: "в Бодруме", en: "in Bodrum" } },
  { slug: "mykonos", name: "Миконос", nameEn: "Mykonos", country: { ru: "Греция", en: "Greece" }, image: IMG.mykonos, sym: "€", loc: { ru: "на Миконосе", en: "in Mykonos" } },
  { slug: "monaco", name: "Монако", nameEn: "Monaco", country: { ru: "Монако", en: "Monaco" }, image: IMG.monaco, sym: "€", loc: { ru: "в Монако", en: "in Monaco" } },
  { slug: "saint-tropez", name: "Сен-Тропе", nameEn: "Saint-Tropez", country: { ru: "Франция", en: "France" }, image: IMG["saint-tropez"], sym: "€", loc: { ru: "в Сен-Тропе", en: "in Saint-Tropez" } },
];

const OCCASION_ICONS = [Heart, Gift, Sparkles, Briefcase, Anchor];

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}
         style={{ opacity: shown ? 1 : 0, transform: shown ? "translateY(0)" : "translateY(26px)", transition: `opacity 0.7s ease ${delay}ms, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}ms` }}>
      {children}
    </div>
  );
}

function getLang() {
  return getLangFromWindow(["ru", "en"]);
}

const COPY = {
  ru: {
    title: "Цветочное оформление яхт — Бодрум, Миконос, Монако и Сен-Тропе | YUCCA Flowers",
    description: "Премиум цветочное оформление яхт в Бодруме, на Миконосе, в Монако и Сен-Тропе: предложения руки и сердца, дни рождения, годовщины и чартеры. Композиции на палубу, арки, лепестки и свечи. Доставка в марины и на яхты на якоре.",
    keywords: "цветочное оформление яхт, цветы на яхту, декор яхты, предложение на яхте, цветы Бодрум яхта, цветы Миконос яхта, цветы Монако яхта, цветы Сен-Тропе яхта, yacht flowers",
    eyebrow: "ЦВЕТЫ ДЛЯ ЯХТ",
    heroTitle: "Цветы, которые создают",
    heroTitleAccent: "незабываемые моменты на воде",
    heroSubtitle: "Люксовое цветочное оформление яхт — от романтических предложений до дней рождения и корпоративов. Мы создаём захватывающие флористические сеты, которые преображают каждый момент на борту.",
    ctaPrimary: "ЗАПРОСИТЬ СМЕТУ",
    ctaSecondary: "СМОТРЕТЬ ГОРОДА",
    occasionsEyebrow: "ЧТО МЫ ОФОРМЛЯЕМ",
    occasionsTitle: "Оформление яхты под",
    occasionsTitleAccent: "любой повод",
    occasionsSubtitle: "От одной эффектной композиции до полной инсталляции с аркой, лепестками и свечами — мы оформим борт под ваш сценарий.",
    occasions: [
      { title: "Предложение руки и сердца", text: "Палуба в розах и лепестках, свечи, арка или надпись «MARRY ME». Согласуем момент с капитаном." },
      { title: "День рождения", text: "Яркое, но элегантное оформление стола и зоны отдыха, шары и торт по желанию." },
      { title: "Годовщина и романтика", text: "Ужин на закате, композиции на нос яхты и обеденный стол, лепестки в каюте." },
      { title: "Корпоратив и чартер", text: "Сдержанный статусный декор для деловых выходов, встречи партнёров и мероприятий на воде." },
      { title: "Встреча гостей", text: "Приветственные букеты и композиции к прибытию гостей на борт или на виллу у причала." },
    ],
    citiesEyebrow: "ГДЕ МЫ РАБОТАЕМ",
    citiesTitle: "Оформление яхт в",
    citiesTitleAccent: "четырёх столицах Средиземноморья",
    citiesSubtitle: "Бодрум, Миконос, Монако и Сен-Тропе — возим во все основные марины и прямо на яхты на якоре. Откройте каталог города, чтобы выбрать композиции.",
    productsEyebrow: "ПОПУЛЯРНОЕ ДЛЯ ЯХТ",
    productsTitle: "Премиум-композиции",
    productsTitleAccent: "для борта",
    productsSubtitle: "Большие статусные букеты и корзины, которые эффектно смотрятся на палубе. Полный каталог — в разделе города.",
    productsCta: "Обсудить оформление в WhatsApp",
    fromLabel: "",
    valueEyebrow: "ПОЧЕМУ YUCCA",
    valueTitle: "Оформление яхт,",
    valueTitleAccent: "которому доверяют",
    valueSubtitle: "Свежие премиум-цветы, устойчивые к морю и жаре, команды флористов на месте и полное сопровождение на русском и английском.",
    values: [
      { title: "Премиум-качество", text: "Свежие цветы, красиво собранные, стойкие сорта для морского воздуха и жары." },
      { title: "Доставка на борт", text: "В марины Бодрума, Миконоса, Монако и Сен-Тропе и прямо на яхту на якоре тендером." },
      { title: "Индивидуальный дизайн", text: "Под ваш повод, палитру и размер яхты — без шаблонов." },
      { title: "Точно ко времени", text: "Согласуем момент с капитаном и стюардом, доставляем без задержек." },
      { title: "Поддержка 24/7", text: "На связи в WhatsApp на русском и английском в любое время." },
    ],
    hookQuote: "Море — лучшая сцена.",
    hookQuoteAccent: "Цветы делают её незабываемой.",
    hookSubtitle: "Мы оформляем яхты в четырёх главных средиземноморских направлениях — Бодрум, Миконос, Монако и Сен-Тропе. Предложения на закате, дни рождения на палубе, годовщины в открытом море. Один стандарт качества, где бы вы ни бросили якорь.",
    hookStats: [
      { value: "4", label: "города присутствия" },
      { value: "15+", label: "марин и причалов" },
      { value: "24ч", label: "срок ответа со сметой" },
      { value: "5.0", label: "средний рейтинг" },
    ],
    partnersEyebrow: "МАРИНЫ И ПРИЧАЛЫ",
    partnersTitle: "Работаем во всех ключевых маринах Бодрума, Миконоса, Монако и Сен-Тропе",
    partners: [
      "BODRUM MARINA", "YALIKAVAK PALMARINA", "TURGUTREIS MARINA", "MYKONOS MARINA",
      "PORT HERCULE", "PORT DE FONTVIEILLE", "PORT DE SAINT-TROPEZ", "LES MARINES DE GASSIN",
      "D-MARIN", "MILTA BODRUM", "ORNOS BAY", "PSAROU",
    ],
    bottomTitle: "Расскажите о вашем",
    bottomTitleAccent: "моменте на воде",
    bottomSubtitle: "Бесплатная консультация и идеи оформления в течение нескольких часов. Назовите город, марину или яхту — остальное мы возьмём на себя.",
    contactCta: "СВЯЗАТЬСЯ С НАМИ",
    seoEyebrow: "ЦВЕТОЧНОЕ ОФОРМЛЕНИЕ ЯХТ С YUCCA",
    seoTitle: "Цветы на яхту в",
    seoTitleAccent: "Бодруме, Миконосе, Монако и Сен-Тропе",
    seoBlocks: [
      {
        heading: "Зачем оформлять яхту цветами",
        content: "Яхта — одна из самых сильных сцен для важного момента: море, закат, приватность и вид, которого нет на берегу. Цветочное оформление превращает обычный выход в историю. Мы оформляем нос яхты, леера, обеденный стол на палубе, кокпит, зону лежаков и каюту — от одной эффектной композиции до полной инсталляции с аркой, лепестками и свечами. Именно детали, а не размах, делают вечер на воде незабываемым.",
      },
      {
        heading: "Оформление яхты для предложения руки и сердца",
        content: "Предложение на борту — наш самый частый сценарий. Мы оформляем палубу розами и лепестками, добавляем свечи, цветочную арку или надпись «MARRY ME», согласуем время с капитаном и доставляем всё незаметно, чтобы сюрприз сохранился до нужной секунды. Закат в открытом море, шампанское и цветы, которых человек не ждал, — это тот случай, когда всё складывается идеально.",
      },
      {
        heading: "Марины и логистика: Бодрум, Миконос, Монако и Сен-Тропе",
        content: "В Бодруме мы работаем во всех ключевых маринах — Bodrum Marina, Yalıkavak Palmarina, Turgutreis, Milta и D-Marin, а также доставляем прямо на яхту на якоре в бухтах Гюмбета, Торбы и Ялыкавака. На Миконосе — Mykonos Marina (Турлос), Старый порт, бухты Орнос и Псару. В Монако — Port Hercule и Port de Fontvieille, включая яхты у входа в порт во время Гран-при и Yacht Show. В Сен-Тропе — Vieux Port и Les Marines de Gassin. Мы согласуем причал, номер понтона, имя капитана или стюарда и точное время, учитывая ограничения доступа в порт. Такой сценарий лучше планировать хотя бы за день.",
      },
      {
        heading: "Сколько стоит оформление яхты и как заказать",
        content: "Бюджет зависит от размера яхты, числа зон и выбранных цветов. Компактные романтические сеты — примерно от €300-500, полное оформление предложения или дня рождения — от €800-1 500, крупные праздничные инсталляции — от €2 500. Мы подбираем стойкие, устойчивые к жаре сорта — розы, орхидеи, гортензии, лизиантусы и зелень — и присылаем фото композиции перед доставкой. Оплатить можно картой любой страны, криптовалютой или переводом Wise/SWIFT.",
      },
    ],
    seoFinal: "YUCCA — команда флористов с опытом премиум-оформления на воде в Бодруме, на Миконосе, в Монако и Сен-Тропе. Предложение на закате, день рождения на палубе, годовщина в открытом море или корпоративный чартер — мы создадим оформление, которое запомнится. Напишите нам в WhatsApp с городом, мариной или именем яхты, датой и поводом — пришлём идеи и смету в течение нескольких часов.",
    seoCta: "Запросить идеи и смету",
    breadcrumb: "Оформление яхт",
    faqTitle: "Цветочное оформление яхт — частые вопросы",
    lead: {
      eyebrow: "ЗАЯВКА НА ОФОРМЛЕНИЕ",
      title: "Расскажите о вашей",
      titleAccent: "яхте и поводе",
      subtitle: "Оставьте заявку — пришлём идеи оформления и смету в течение нескольких часов. Или напишите нам напрямую в WhatsApp.",
      nameLabel: "Имя", namePh: "Ваше имя",
      contactLabel: "Контакт", contactPh: "WhatsApp, телефон или e-mail",
      dateLabel: "Дата", datePh: "Когда нужно оформление",
      cityLabel: "Город", messageLabel: "Детали",
      messagePh: "Марина или яхта, повод, пожелания по цветам и бюджету",
      submit: "ОТПРАВИТЬ ЗАЯВКУ", sending: "ОТПРАВЛЯЕМ…", orWhatsApp: "НАПИСАТЬ В WHATSAPP",
      required: "Укажите имя и контакт для связи.",
      errorMsg: "Не удалось отправить. Попробуйте ещё раз или напишите в WhatsApp.",
      successTitle: "Заявка отправлена!",
      successMsg: "Мы свяжемся с вами в ближайшее время с идеями оформления и сметой.",
    },
  },
  en: {
    title: "Yacht Flowers — Luxury Floral Decoration in Bodrum, Mykonos, Monaco & Saint-Tropez | YUCCA",
    description: "Premium flower decoration for yachts in Bodrum, Mykonos, Monaco and Saint-Tropez: proposals, birthdays, anniversaries and charters. Deck arrangements, arches, petals and candles. Delivery to marinas and yachts at anchor.",
    keywords: "yacht flowers, flowers for yacht, yacht decoration, proposal on yacht, Bodrum yacht flowers, Mykonos yacht flowers, Monaco yacht flowers, Saint-Tropez yacht flowers, luxury yacht floral design",
    eyebrow: "YACHT FLOWERS",
    heroTitle: "Flowers that create",
    heroTitleAccent: "unforgettable moments on the water",
    heroSubtitle: "Luxury floral decoration for yachts — from romantic proposals to birthday celebrations and corporate charters. We design breathtaking floral setups that elevate every moment on board.",
    ctaPrimary: "GET A QUOTE",
    ctaSecondary: "VIEW CITIES",
    occasionsEyebrow: "WHAT WE STYLE",
    occasionsTitle: "Yacht decoration for",
    occasionsTitleAccent: "every occasion",
    occasionsSubtitle: "From a single statement centerpiece to a full installation with an arch, petals and candles — we style the deck around your scenario.",
    occasions: [
      { title: "Marriage proposal", text: "A deck in roses and petals, candles, an arch or a 'MARRY ME' setup. We coordinate the moment with the captain." },
      { title: "Birthday", text: "Bright yet elegant styling of the table and lounge area, balloons and cake on request." },
      { title: "Anniversary & romance", text: "A sunset dinner, arrangements on the bow and dining table, petals in the cabin." },
      { title: "Corporate & charter", text: "Restrained, statement décor for business outings, partner welcomes and events on the water." },
      { title: "Welcome on board", text: "Welcome bouquets and arrangements for guests arriving on board or at a villa by the berth." },
    ],
    citiesEyebrow: "WHERE WE WORK",
    citiesTitle: "Yacht flowers in",
    citiesTitleAccent: "four Mediterranean capitals",
    citiesSubtitle: "Bodrum, Mykonos, Monaco and Saint-Tropez — we deliver to all the main marinas and directly onto yachts at anchor. Open the city catalog to choose arrangements.",
    productsEyebrow: "POPULAR FOR YACHTS",
    productsTitle: "Premium arrangements",
    productsTitleAccent: "for the deck",
    productsSubtitle: "Large statement bouquets and baskets that look stunning on deck. See the full catalog in the city section.",
    productsCta: "Discuss your setup on WhatsApp",
    fromLabel: "from",
    valueEyebrow: "WHY YUCCA",
    valueTitle: "Yacht floristry",
    valueTitleAccent: "you can trust",
    valueSubtitle: "Fresh premium flowers that hold up to sea air and heat, florist teams on the ground and full support in English and Russian.",
    values: [
      { title: "Premium quality", text: "Fresh, beautifully arranged flowers in hardy varieties for sea air and heat." },
      { title: "Delivery on board", text: "To the marinas of Bodrum, Mykonos, Monaco and Saint-Tropez and directly onto yachts at anchor by tender." },
      { title: "Custom design", text: "Tailored to your occasion, palette and yacht size — no templates." },
      { title: "Right on time", text: "We coordinate the moment with the captain and steward and deliver without delays." },
      { title: "24/7 support", text: "Reachable on WhatsApp in English and Russian at any time." },
    ],
    hookQuote: "The sea is the perfect stage.",
    hookQuoteAccent: "Flowers make it unforgettable.",
    hookSubtitle: "We style yachts in four of the Mediterranean's leading destinations — Bodrum, Mykonos, Monaco and Saint-Tropez. Sunset proposals, deck birthdays, anniversaries on the open sea. One standard of excellence, wherever you drop anchor.",
    hookStats: [
      { value: "4", label: "cities served" },
      { value: "15+", label: "marinas & berths" },
      { value: "24h", label: "quote turnaround" },
      { value: "5.0", label: "average rating" },
    ],
    partnersEyebrow: "MARINAS & BERTHS",
    partnersTitle: "We work in all the key marinas of Bodrum, Mykonos, Monaco and Saint-Tropez",
    partners: [
      "BODRUM MARINA", "YALIKAVAK PALMARINA", "TURGUTREIS MARINA", "MYKONOS MARINA",
      "PORT HERCULE", "PORT DE FONTVIEILLE", "PORT DE SAINT-TROPEZ", "LES MARINES DE GASSIN",
      "D-MARIN", "MILTA BODRUM", "ORNOS BAY", "PSAROU",
    ],
    bottomTitle: "Tell us about your",
    bottomTitleAccent: "moment on the water",
    bottomSubtitle: "A free consultation and styling ideas within a few hours. Name the city, marina or yacht — we'll handle the rest.",
    contactCta: "CONTACT US",
    seoEyebrow: "YACHT FLORAL DECORATION WITH YUCCA",
    seoTitle: "Flowers for yachts in",
    seoTitleAccent: "Bodrum, Mykonos, Monaco & Saint-Tropez",
    seoBlocks: [
      {
        heading: "Why decorate a yacht with flowers",
        content: "A yacht is one of the strongest stages for a big moment: the sea, the sunset, the privacy and a view you can't get ashore. Floral decoration turns an ordinary outing into a story. We style the bow, the railings, the deck dining table, the cockpit, the sunbed area and the cabin — from a single statement piece to a full installation with an arch, petals and candles. It's the details, not the scale, that make an evening on the water unforgettable.",
      },
      {
        heading: "Yacht decoration for a marriage proposal",
        content: "A proposal on board is our most requested scenario. We style the deck with roses and petals, add candles, a floral arch or a 'MARRY ME' letter setup, coordinate timing with the captain and deliver everything discreetly so the surprise stays intact until the moment. Sunset on the open sea, champagne and flowers no one expected — this is when everything comes together perfectly.",
      },
      {
        heading: "Marinas and logistics: Bodrum, Mykonos, Monaco & Saint-Tropez",
        content: "In Bodrum we work in all the key marinas — Bodrum Marina, Yalikavak Palmarina, Turgutreis, Milta and D-Marin — and deliver directly onto yachts at anchor in the bays of Gümbet, Torba and Yalikavak. In Mykonos — Mykonos Marina (Tourlos), the Old Port, and Ornos and Psarou bays. In Monaco — Port Hercule and Port de Fontvieille, including yachts at the port entrance during the Grand Prix and Yacht Show. In Saint-Tropez — the Vieux Port and Les Marines de Gassin. We arrange the berth, pontoon number, captain's or steward's name and exact time, accounting for port access restrictions. This kind of setup is best planned at least a day ahead.",
      },
      {
        heading: "How much does yacht decoration cost and how to order",
        content: "The budget depends on the yacht size, the number of zones and the flowers chosen. Compact romantic setups start from around €300-500, full proposal or birthday stylings from €800-1,500, and large celebration installations from €2,500+. We select hardy, heat-tolerant varieties — roses, orchids, hydrangeas, lisianthus and greenery — and send a photo of the arrangement before delivery. You can pay by card from any country, cryptocurrency or a Wise/SWIFT transfer.",
      },
    ],
    seoFinal: "YUCCA is a team of florists with experience in premium on-the-water styling in Bodrum, Mykonos, Monaco and Saint-Tropez. A sunset proposal, a deck birthday, an anniversary on the open sea or a corporate charter — we'll create decoration that's remembered. Message us on WhatsApp with the city, marina or yacht name, the date and the occasion — we'll reply with ideas and a quote within a few hours.",
    seoCta: "Get ideas and a quote",
    breadcrumb: "Yacht Flowers",
    faqTitle: "Yacht flowers — frequently asked questions",
    lead: {
      eyebrow: "REQUEST A QUOTE",
      title: "Tell us about your",
      titleAccent: "yacht and occasion",
      subtitle: "Leave a request — we'll reply with styling ideas and a quote within a few hours. Or message us directly on WhatsApp.",
      nameLabel: "Name", namePh: "Your name",
      contactLabel: "Contact", contactPh: "WhatsApp, phone or e-mail",
      dateLabel: "Date", datePh: "When you need it",
      cityLabel: "City", messageLabel: "Details",
      messagePh: "Marina or yacht, occasion, flower and budget preferences",
      submit: "SEND REQUEST", sending: "SENDING…", orWhatsApp: "MESSAGE ON WHATSAPP",
      required: "Please provide your name and contact.",
      errorMsg: "Couldn't send. Please try again or message us on WhatsApp.",
      successTitle: "Request sent!",
      successMsg: "We'll be in touch shortly with styling ideas and a quote.",
    },
  },
};

function SeoTags({ lang, t }) {
  useEffect(() => {
    document.title = t.title;
    const set = (n, c) => {
      let el = document.querySelector(`meta[name="${n}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", n); document.head.appendChild(el); }
      el.setAttribute("content", c);
    };
    set("description", t.description);
    set("keywords", t.keywords);
    const origin = window.location.origin;
    const ogImg = `${origin}/yachts/hero.webp`;
    const setProp = (p, c) => {
      let el = document.querySelector(`meta[property="${p}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", p); document.head.appendChild(el); }
      el.setAttribute("content", c);
    };
    setProp("og:title", t.title);
    setProp("og:description", t.description);
    setProp("og:type", "website");
    setProp("og:url", `${origin}/yachts${lang !== "ru" ? `?lang=${lang}` : ""}`);
    setProp("og:image", ogImg);
    set("twitter:card", "summary_large_image");
    set("twitter:title", t.title);
    set("twitter:description", t.description);
    set("twitter:image", ogImg);
    const link = (rel, href, hreflang) => {
      const sel = `link[rel="${rel}"]${hreflang ? `[hreflang="${hreflang}"]` : ""}`;
      let el = document.querySelector(sel);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); if (hreflang) el.setAttribute("hreflang", hreflang); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    link("canonical", `${origin}/yachts${lang !== "ru" ? `?lang=${lang}` : ""}`);
    link("alternate", `${origin}/yachts`, "ru");
    link("alternate", `${origin}/yachts?lang=en`, "en");
  }, [lang, t]);

  const origin = typeof window !== "undefined" ? window.location.origin : "https://yuccaflower.com";
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: t.title,
        description: t.description,
        serviceType: lang === "en" ? "Yacht floral decoration" : "Цветочное оформление яхт",
        provider: { "@type": "LocalBusiness", name: "YUCCA Flowers", url: "https://yuccaflower.com" },
        areaServed: [
          { "@type": "City", name: lang === "en" ? "Bodrum" : "Бодрум" },
          { "@type": "City", name: lang === "en" ? "Mykonos" : "Миконос" },
        ],
        offers: { "@type": "AggregateOffer", priceCurrency: "EUR", lowPrice: "300", highPrice: "5000" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: lang === "en" ? "Home" : "Главная", item: `${origin}/${lang !== "ru" ? `?lang=${lang}` : ""}` },
          { "@type": "ListItem", position: 2, name: t.breadcrumb, item: `${origin}/yachts${lang !== "ru" ? `?lang=${lang}` : ""}` },
        ],
      },
    ],
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />;
}

function YachtFaq({ items, title }) {
  const [openFaq, setOpenFaq] = useState(0);
  return (
    <section className="relative overflow-hidden bg-[#FFFCFD] py-16 sm:py-24 lg:py-28" data-testid="yacht-hub-faq">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: items.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }) }}
      />
      <div className="absolute -top-16 right-[-8%] w-[400px] h-[400px] rounded-full bg-[#F6C9CF]/30 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-[-8%] w-[380px] h-[380px] rounded-full bg-[#E39FAC]/15 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-10">
        <div className="text-center mb-10 sm:mb-14">
          <p className="text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] text-[#C25E72] mb-3 sm:mb-4 font-medium">FAQ</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#1F1815] leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
            {title}
          </h2>
          <div className="w-12 sm:w-16 h-px bg-[#E39FAC] mx-auto mt-5 sm:mt-6" />
        </div>

        <div className="space-y-3 sm:space-y-4">
          {items.map((f, i) => {
            const isOpen = openFaq === i;
            return (
              <div key={i}
                   className={`rounded-2xl border transition-colors duration-200 ${isOpen ? "bg-white border-[#E39FAC] shadow-[0_10px_30px_-14px_rgba(194,94,114,0.35)]" : "bg-white/70 border-[#F3DDE1] hover:border-[#E8B4B8]"}`}
                   data-testid={`yacht-hub-faq-item-${i}`}>
                <button onClick={() => setOpenFaq(isOpen ? -1 : i)}
                        className="w-full text-left px-5 sm:px-7 py-4 sm:py-5 flex items-center justify-between gap-4"
                        data-testid={`yacht-hub-faq-toggle-${i}`}>
                  <span className="font-medium text-[#1F1815] text-[14px] sm:text-[16px] leading-snug">{f.q}</span>
                  <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-200 ${isOpen ? "bg-[#C25E72] text-white rotate-45" : "bg-[#FFF0F2] text-[#C25E72]"}`}>
                    <Plus className="w-4 h-4" strokeWidth={2} />
                  </span>
                </button>
                {isOpen && (
                  <p className="px-5 sm:px-7 pb-5 sm:pb-6 text-[#6B5D4F] leading-relaxed font-light text-[14px] sm:text-[15px]" data-testid={`yacht-hub-faq-answer-${i}`}>{f.a}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function YachtsHubPage() {
  const [lang, setLang] = useState(getLang());
  const t = COPY[lang];

  const [yachtFaq, setYachtFaq] = useState([]);
  const [products, setProducts] = useState([]);
  useEffect(() => {
    let active = true;
    Promise.all(
      YACHT_CITIES.map((c) =>
        axios.get("/api/products", { params: { category: "premium", city: c.slug } })
          .then((r) => (r.data || [])
            .filter((p) => !/медвед|мишк|bear|teddy/i.test(`${p.name || ""} ${p.name_en || ""}`))
            .map((p) => ({ ...p, _sym: c.sym, _city: c.slug })))
          .catch(() => [])
      )
    ).then((lists) => {
      if (!active) return;
      // interleave all cities so each is represented, take 8
      const merged = [];
      const maxLen = Math.max(...lists.map((l) => l.length), 0);
      for (let i = 0; i < maxLen; i++) {
        lists.forEach((l) => { if (l[i]) merged.push(l[i]); });
      }
      setProducts(merged.slice(0, 8));
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    axios
      .get("/api/faq", { params: { kind: "yacht-hub", lang } })
      .then((r) => { if (active) setYachtFaq(r.data.items || []); })
      .catch(() => { if (active) setYachtFaq([]); });
    return () => { active = false; };
  }, [lang]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const urlLang = new URLSearchParams(window.location.search).get("lang");
    if (urlLang === lang) return;
    if (lang === "ru" && !urlLang) return;
    setLangInWindowUrl(lang);
  }, [lang]);

  const switchLang = () => {
    const next = lang === "ru" ? "en" : "ru";
    setLang(next);
    setLangInWindowUrl(next);
  };

  return (
    <div className="bg-white" data-testid="yachts-hub-page">
      <SeoTags lang={lang} t={t} />

      {/* Top nav */}
      <nav className="absolute top-0 left-0 right-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-4 sm:py-5 flex items-center justify-between">
          <div className="text-[#1F1815]"><Logo /></div>
          <div className="hidden lg:flex items-center gap-8 text-[12px] tracking-[0.2em] text-[#3D2F22]/80 font-light">
            <Link to={`/${lang !== "ru" ? `?lang=${lang}` : ""}`} className="hover:text-[#1F1815]">{lang === "en" ? "SHOP" : "МАГАЗИН"}</Link>
            <Link to={`/weddings${lang !== "ru" ? `?lang=${lang}` : ""}`} className="hover:text-[#1F1815]">{lang === "en" ? "WEDDINGS" : "СВАДЬБЫ"}</Link>
            <span className="text-[#1F1815] font-medium border-b border-[#E39FAC] pb-1">{lang === "en" ? "YACHTS" : "ЯХТЫ"}</span>
            <Link to={`/blog${lang !== "ru" ? `?lang=${lang}` : ""}`} className="hover:text-[#1F1815]">{lang === "en" ? "BLOG" : "БЛОГ"}</Link>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={switchLang} className="text-[11px] sm:text-[12px] tracking-[0.2em] text-[#3D2F22]/80 hover:text-[#1F1815] px-2" data-testid="yacht-hub-lang-switch">
              {lang === "en" ? "EN" : "RU"}
            </button>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer"
               className="hidden md:inline-flex items-center px-6 py-3 bg-[#1F1815] text-white text-[11px] tracking-[0.25em] font-medium hover:bg-black transition rounded-xl">
              {t.ctaPrimary}
            </a>
            <WeddingMobileMenu lang={lang} />
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[560px] sm:h-[680px] lg:h-[720px] overflow-hidden" data-testid="yacht-hub-hero">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt={t.title} className="absolute inset-0 w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/96 via-white/82 to-white/45" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/45 via-transparent to-[#FFFCFD]" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 sm:px-10 h-full flex flex-col justify-center">
          <p className="text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] text-[#C25E72] mb-5 sm:mb-8 font-medium">{t.eyebrow}</p>
          <h1 className="text-[#1F1815] text-4xl sm:text-5xl lg:text-7xl leading-[1.05] mb-5 sm:mb-8 max-w-3xl"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, textShadow: "0 1px 18px rgba(255,255,255,0.85)" }}>
            {t.heroTitle}<br />
            <span className="italic font-light text-[#C25E72]" style={{ textShadow: "0 2px 20px rgba(255,255,255,0.95), 0 1px 3px rgba(255,255,255,0.9)" }}>{t.heroTitleAccent}</span>
          </h1>
          <p className="text-[#3D2F22]/85 text-sm sm:text-base lg:text-lg leading-relaxed max-w-xl mb-7 sm:mb-10 font-light">{t.heroSubtitle}</p>
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <a href="#request"
               className="group inline-flex items-center gap-2 sm:gap-3 px-7 sm:px-9 py-3.5 sm:py-4 bg-gradient-to-r from-[#F2B8C4] via-[#E39FAC] to-[#C25E72] hover:from-[#F7C6CF] hover:to-[#D98096] text-[#1F1815] text-[11px] sm:text-[12px] tracking-[0.22em] sm:tracking-[0.28em] font-semibold transition-all duration-500 rounded-xl shadow-[0_12px_40px_-12px_rgba(194,94,114,0.6)] hover:shadow-[0_18px_55px_-12px_rgba(194,94,114,0.8)] hover:-translate-y-0.5"
               data-testid="yacht-hub-hero-cta">
              <MessageCircle className="w-4 h-4" />
              <span>{t.ctaPrimary}</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a href="#cities"
               className="inline-flex items-center px-6 sm:px-8 py-3.5 sm:py-4 border border-[#1F1815]/40 text-[#1F1815] text-[11px] sm:text-[12px] tracking-[0.2em] sm:tracking-[0.25em] font-medium hover:bg-[#1F1815]/5 hover:border-[#1F1815] transition rounded-xl">
              {t.ctaSecondary}
            </a>
          </div>
        </div>
      </section>

      {/* Hook — stats */}
      <section className="relative bg-[#FFFCFD] text-[#1F1815] overflow-hidden py-20 sm:py-28 lg:py-32" data-testid="yacht-hub-hook">
        <div className="absolute inset-0 pointer-events-none">
          <img src={IMG.hero} alt="" className="w-full h-full object-cover blur-[64px] scale-125 opacity-[0.16]" />
          <div className="absolute inset-0 bg-[#FFFCFD]/80" />
        </div>
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#F6C9CF]/40 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#E39FAC]/20 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.5] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(#F3DDE1 0.5px, transparent 0.5px)", backgroundSize: "22px 22px" }} />

        <div className="relative max-w-4xl mx-auto px-5 sm:px-10 text-center">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-7 sm:mb-9">
            <span className="w-10 sm:w-16 h-px bg-[#E39FAC]" />
            <Diamond className="w-3 h-3 sm:w-4 sm:h-4 text-[#C25E72]" />
            <span className="w-10 sm:w-16 h-px bg-[#E39FAC]" />
          </div>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl leading-[1.1] mb-5 sm:mb-7"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
            <span className="text-[#1F1815]">{t.hookQuote}</span><br />
            <span className="italic text-[#C25E72]">{t.hookQuoteAccent}</span>
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-[#6B5D4F] font-light max-w-2xl mx-auto leading-relaxed mb-12 sm:mb-16">
            {t.hookSubtitle}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 sm:gap-y-10 border-t border-[#F3DDE1] pt-10 sm:pt-12">
            {t.hookStats.map((s, i) => (
              <div key={i} className="text-center" data-testid={`yacht-hub-stat-${i}`}>
                <div className="text-4xl sm:text-5xl lg:text-6xl text-[#C25E72] mb-2 sm:mb-3"
                     style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>{s.value}</div>
                <div className="text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.25em] text-[#A87E88] uppercase font-light">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Occasions */}
      <section className="relative overflow-hidden bg-[#FFF6F8] py-16 sm:py-24 lg:py-32" data-testid="yacht-hub-occasions">
        <div className="absolute -top-32 right-[-10%] w-[520px] h-[520px] rounded-full bg-[#F6C9CF]/35 blur-3xl pointer-events-none" />
        <div className="absolute bottom-[-15%] left-[-8%] w-[460px] h-[460px] rounded-full bg-[#E39FAC]/20 blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="text-center mb-10 sm:mb-14">
            <p className="text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] text-[#C25E72] mb-3 sm:mb-4 font-light">{t.occasionsEyebrow}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#1F1815] leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
              {t.occasionsTitle} <span className="italic text-[#C25E72]">{t.occasionsTitleAccent}</span>
            </h2>
            <p className="text-sm sm:text-base text-[#6B5D4F] font-light mt-4 sm:mt-5 max-w-2xl mx-auto leading-relaxed">{t.occasionsSubtitle}</p>
            <div className="w-12 sm:w-16 h-px bg-[#E39FAC] mx-auto mt-5 sm:mt-6" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 sm:gap-6">
            {t.occasions.map((o, i) => {
              const Icon = OCCASION_ICONS[i] || Sparkles;
              return (
                <Reveal key={i} delay={i * 90}>
                  <div className="group bg-white border border-[#F3DDE1] rounded-xl p-6 sm:p-7 text-center hover:shadow-[0_22px_50px_-20px_rgba(194,94,114,0.5)] hover:border-[#E39FAC] hover:-translate-y-1.5 transition-all duration-500 h-full" data-testid={`yacht-hub-occasion-${i}`}>
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#FFF6F8] border border-[#F3DDE1] flex items-center justify-center mx-auto mb-4 sm:mb-5 group-hover:bg-[#C25E72] group-hover:border-[#C25E72] transition-colors duration-500">
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#C25E72] group-hover:text-white transition-colors duration-500" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-sm sm:text-base font-medium text-[#1F1815] mb-2 sm:mb-3">{o.title}</h3>
                    <p className="text-xs sm:text-sm text-[#6B5D4F] font-light leading-relaxed">{o.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partners / marinas marquee */}
      <section className="bg-[#FFF6F8] py-10 sm:py-14 border-y border-[#F3DDE1] overflow-hidden" data-testid="yacht-hub-partners">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 text-center mb-7 sm:mb-9">
          <p className="text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] text-[#C25E72] mb-2 sm:mb-3 font-medium">{t.partnersEyebrow}</p>
          <p className="text-xs sm:text-sm text-[#6B5D4F] font-light max-w-xl mx-auto">{t.partnersTitle}</p>
        </div>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-r from-[#FFF6F8] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-l from-[#FFF6F8] to-transparent z-10 pointer-events-none" />
          <div className="flex gap-10 sm:gap-16 animate-marquee whitespace-nowrap will-change-transform">
            {[...t.partners, ...t.partners].map((p, i) => (
              <span key={i} className="text-[11px] sm:text-[13px] tracking-[0.25em] sm:tracking-[0.3em] text-[#A8967E] hover:text-[#C25E72] transition font-medium uppercase select-none">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Cities */}
      <section id="cities" className="relative bg-[#FFFCFD] py-16 sm:py-24 lg:py-32 overflow-hidden" data-testid="yacht-hub-cities">
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-10">
          <div className="text-center mb-10 sm:mb-14">
            <p className="text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] text-[#C25E72] mb-3 sm:mb-4 font-medium">{t.citiesEyebrow}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#1F1815] leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
              {t.citiesTitle} <span className="italic text-[#C25E72]">{t.citiesTitleAccent}</span>
            </h2>
            <p className="text-sm sm:text-base text-[#6B5D4F] font-light mt-4 sm:mt-5 max-w-2xl mx-auto leading-relaxed">{t.citiesSubtitle}</p>
            <div className="w-12 sm:w-16 h-px bg-[#E39FAC] mx-auto mt-5 sm:mt-6" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {YACHT_CITIES.map((city, ci) => {
              const name = lang === "en" ? city.nameEn : city.name;
              const country = city.country[lang] || city.country.en;
              return (
                <Reveal key={city.slug} delay={ci * 80}>
                <Link to={`/${city.slug}${lang !== "ru" ? `?lang=${lang}` : ""}`}
                      className="group relative overflow-hidden rounded-xl bg-[#1F1815] aspect-[3/4] sm:aspect-[4/5] flex flex-col justify-end ring-1 ring-[#E39FAC]/40 hover:ring-[#C25E72] hover:shadow-[0_18px_45px_-15px_rgba(194,94,114,0.45)] transition-all duration-500"
                      data-testid={`yacht-hub-city-${city.slug}`}>
                  <img src={city.image} alt={`${name} yacht floral decoration`}
                       className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0807]/85 via-[#0A0807]/20 to-transparent" />
                  <div className="relative p-4 sm:p-5 text-white">
                    <p className="text-[8px] sm:text-[9px] tracking-[0.22em] sm:tracking-[0.28em] text-[#F6C9CF] mb-1 sm:mb-1.5">{country.toUpperCase()}</p>
                    <h3 className="text-lg sm:text-2xl mb-1.5 sm:mb-2 leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>{name}</h3>
                    <div className="inline-flex items-center gap-1 sm:gap-1.5 text-[9px] sm:text-[10px] tracking-[0.18em] sm:tracking-[0.22em] text-[#F6C9CF] group-hover:gap-2 transition-all">
                      {lang === "en" ? "EXPLORE" : "ОТКРЫТЬ"} <ArrowRight className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    </div>
                  </div>
                </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular products for yachts */}
      {products.length > 0 && (
        <section className="relative overflow-hidden bg-white py-16 sm:py-24 lg:py-28" data-testid="yacht-hub-products">
          <div className="absolute -top-16 left-[-8%] w-[420px] h-[420px] rounded-full bg-[#F6C9CF]/25 blur-3xl pointer-events-none" />
          <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-10">
            <div className="text-center mb-10 sm:mb-14">
              <p className="text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] text-[#C25E72] mb-3 sm:mb-4 font-medium">{t.productsEyebrow}</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#1F1815] leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
                {t.productsTitle} <span className="italic text-[#C25E72]">{t.productsTitleAccent}</span>
              </h2>
              <p className="text-sm sm:text-base text-[#6B5D4F] font-light mt-4 sm:mt-5 max-w-2xl mx-auto leading-relaxed">{t.productsSubtitle}</p>
              <div className="w-12 sm:w-16 h-px bg-[#E39FAC] mx-auto mt-5 sm:mt-6" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {products.map((p, i) => {
                const name = lang === "en" ? (p.name_en || p.name) : p.name;
                const cityObj = YACHT_CITIES.find((c) => c.slug === p._city) || YACHT_CITIES[0];
                const cityName = lang === "en" ? cityObj.nameEn : cityObj.name;
                const cityLoc = cityObj.loc[lang] || cityObj.loc.en;
                const waText = encodeURIComponent(
                  (lang === "en"
                    ? `Hello! I'm interested in the "${name}" arrangement for a yacht ${cityObj.loc.en}.`
                    : `Здравствуйте! Интересует композиция «${name}» для яхты ${cityLoc}.`)
                );
                return (
                  <Reveal key={p.id || i} delay={(i % 4) * 80}>
                  <a href={`${WA_LINK}?text=${waText}`} target="_blank" rel="noopener noreferrer"
                        className="group bg-white rounded-2xl overflow-hidden border border-[#F3DDE1] hover:border-[#E39FAC] hover:shadow-[0_18px_45px_-20px_rgba(194,94,114,0.45)] hover:-translate-y-1 transition-all duration-500 flex flex-col h-full"
                        data-testid={`yacht-hub-product-${i}`}>
                    <div className="relative aspect-[3/4] overflow-hidden bg-[#FFF6F8]">
                      <img src={p.image} alt={name}
                           className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[9px] tracking-[0.15em] uppercase text-[#C25E72] font-semibold">
                        {cityName}
                      </span>
                    </div>
                    <div className="p-4 sm:p-5 flex-1 flex flex-col">
                      <h3 className="text-[14px] sm:text-[16px] text-[#1F1815] mb-3 leading-snug" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>{name}</h3>
                      <div className="mt-auto pt-2">
                        <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[#C25E72] font-medium group-hover:gap-2.5 transition-all">
                          <MessageCircle className="w-3.5 h-3.5" />
                          {lang === "en" ? "Order on WhatsApp" : "Заказать в WhatsApp"} <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </a>
                  </Reveal>
                );
              })}
            </div>

            <div className="text-center mt-10 sm:mt-12">
              <a href={WA_LINK} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-3.5 border border-[#E39FAC] text-[#C25E72] text-[11px] sm:text-[12px] tracking-[0.2em] font-medium rounded-xl hover:bg-[#FFF0F2] transition"
                    data-testid="yacht-hub-products-cta">
                <MessageCircle className="w-4 h-4" /> {t.productsCta}
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Values */}
      <section className="relative overflow-hidden bg-[#FFF6F8] py-16 sm:py-24 lg:py-32" data-testid="yacht-hub-values">
        <div className="absolute inset-0 pointer-events-none">
          <img src={IMG.mykonos} alt="" className="w-full h-full object-cover blur-[70px] scale-125 opacity-[0.12]" />
          <div className="absolute inset-0 bg-[#FFF6F8]/78" />
        </div>
        <div className="absolute top-1/3 left-[-10%] w-[440px] h-[440px] rounded-full bg-[#F6C9CF]/30 blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="text-center mb-10 sm:mb-14">
            <p className="text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] text-[#C25E72] mb-3 sm:mb-4 font-light">{t.valueEyebrow}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#1F1815] leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
              {t.valueTitle} <span className="italic text-[#C25E72]">{t.valueTitleAccent}</span>
            </h2>
            <p className="text-sm sm:text-base text-[#6B5D4F] font-light mt-4 sm:mt-5 max-w-2xl mx-auto leading-relaxed">{t.valueSubtitle}</p>
            <div className="w-12 sm:w-16 h-px bg-[#E39FAC] mx-auto mt-5 sm:mt-6" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-5 sm:gap-6">
            {t.values.map((v, i) => (
              <Reveal key={i} delay={i * 80}>
              <div className="text-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white border border-[#F3DDE1] flex items-center justify-center mx-auto mb-4 sm:mb-5">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-[#C25E72]" strokeWidth={1.5} />
                </div>
                <h3 className="text-sm sm:text-base font-medium text-[#1F1815] mb-2 sm:mb-3">{v.title}</h3>
                <p className="text-xs sm:text-sm text-[#6B5D4F] font-light leading-relaxed">{v.text}</p>
              </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="relative overflow-hidden bg-white py-16 sm:py-24 lg:py-32" data-testid="yacht-hub-seo">
        <div className="absolute -top-20 left-[-8%] w-[400px] h-[400px] rounded-full bg-[#F6C9CF]/25 blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 right-[-10%] w-[460px] h-[460px] rounded-full bg-[#E39FAC]/15 blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-10">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] text-[#C25E72] mb-3 sm:mb-4 font-light">{t.seoEyebrow}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#1F1815] leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
              {t.seoTitle} <span className="italic text-[#C25E72]">{t.seoTitleAccent}</span>
            </h2>
            <div className="w-12 sm:w-16 h-px bg-[#E39FAC] mx-auto mt-5 sm:mt-7" />
          </div>

          <div className="space-y-10 sm:space-y-12">
            {t.seoBlocks.map((b, i) => (
              <article key={i} data-testid={`yacht-hub-seo-block-${i}`}>
                <h3 className="text-xl sm:text-2xl lg:text-3xl text-[#1F1815] mb-3 sm:mb-4"
                    style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
                  {b.heading}
                </h3>
                <div className="w-8 h-px bg-[#E39FAC] mb-4 sm:mb-5" />
                <p className="text-sm sm:text-base text-[#3D2F22] font-light leading-[1.85]">{b.content}</p>
              </article>
            ))}
          </div>

          <div className="mt-14 sm:mt-16 pt-10 sm:pt-12 border-t border-[#F3DDE1] text-center">
            <p className="text-sm sm:text-base text-[#3D2F22] font-light leading-[1.85] max-w-2xl mx-auto mb-8 sm:mb-10">
              {t.seoFinal}
            </p>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 sm:gap-3 px-7 sm:px-9 py-3.5 sm:py-4 bg-[#0A0807] hover:bg-black text-[#F6C9CF] text-[11px] sm:text-[12px] tracking-[0.22em] sm:tracking-[0.25em] font-medium transition-all rounded-xl hover:gap-3 sm:hover:gap-4"
               data-testid="yacht-hub-seo-cta">
              <MessageCircle className="w-4 h-4" />
              <span>{t.seoCta}</span>
              <ArrowRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>

      {/* Lead form */}
      <HubLeadForm
        lang={lang}
        variant="light"
        service="yacht"
        cities={YACHT_CITIES.map((c) => ({ slug: c.slug, label: lang === "en" ? c.nameEn : c.name }))}
        copy={t.lead}
      />

      {/* FAQ */}
      {yachtFaq.length > 0 && <YachtFaq items={yachtFaq} title={t.faqTitle} />}

      {/* Bottom CTA */}
      <section className="relative bg-[#FFF6F8] text-[#1F1815] overflow-hidden border-t border-[#F3DDE1]" data-testid="yacht-hub-bottom-cta">
        <div className="absolute inset-0 pointer-events-none">
          <img src={IMG.bodrum} alt="" className="w-full h-full object-cover blur-[72px] scale-125 opacity-[0.14]" />
          <div className="absolute inset-0 bg-[#FFF6F8]/78" />
        </div>
        <div className="absolute -top-20 right-[-10%] w-[460px] h-[460px] rounded-full bg-[#F6C9CF]/35 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 left-[-8%] w-[420px] h-[420px] rounded-full bg-[#E39FAC]/18 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.5] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(#F3DDE1 0.5px, transparent 0.5px)", backgroundSize: "26px 26px" }} />

        <div className="relative max-w-4xl mx-auto px-5 sm:px-10 py-20 sm:py-28 lg:py-36 text-center">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-7 sm:mb-9">
            <span className="w-10 sm:w-16 h-px bg-[#E39FAC]" />
            <Diamond className="w-3 h-3 sm:w-4 sm:h-4 text-[#C25E72]" />
            <span className="w-10 sm:w-16 h-px bg-[#E39FAC]" />
          </div>
          <h2 className="text-4xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6 sm:mb-8"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
            <span className="text-[#1F1815]">{t.bottomTitle}</span><br/>
            <span className="italic text-[#C25E72]">{t.bottomTitleAccent}</span>
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-[#6B5D4F] font-light leading-relaxed max-w-xl mx-auto mb-10 sm:mb-12">
            {t.bottomSubtitle}
          </p>
          <a href="#request"
             className="group inline-flex items-center gap-3 px-10 sm:px-14 py-4 sm:py-5 bg-gradient-to-r from-[#F2B8C4] via-[#E39FAC] to-[#C25E72] hover:from-[#F7C6CF] hover:to-[#D98096] text-[#1F1815] text-[12px] sm:text-[13px] tracking-[0.25em] sm:tracking-[0.3em] font-semibold transition-all duration-500 rounded-xl shadow-[0_15px_50px_-12px_rgba(194,94,114,0.6)] hover:shadow-[0_20px_70px_-12px_rgba(194,94,114,0.8)] hover:-translate-y-0.5"
             data-testid="yacht-hub-bottom-cta-btn">
            <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>{t.contactCta}</span>
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </section>

      <YachtsFooter lang={lang} />
    </div>
  );
}
